import { DatesRangeValue } from '@mantine/dates';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import React from 'react';
import {
  AdminStatsDailyPointDto,
  AdminStatsOverviewDto,
} from 'src/api/FiretrolApiClient';
import {
  IStatSectionMetric,
  StatSectionCard,
} from 'src/design/molecules/StatSectionCard';
import { AnswerQualityCard } from 'src/routes/Settings/AIAssistant/components/AnswerQualityCard';
import { CostTrendChart } from 'src/routes/Settings/AIAssistant/components/CostTrendChart';
import { ReasoningEffortTable } from 'src/routes/Settings/AIAssistant/components/ReasoningEffortTable';
import { SqlHealthChart } from 'src/routes/Settings/AIAssistant/components/SqlHealthChart';
import { ToolUseCard } from 'src/routes/Settings/AIAssistant/components/ToolUseCard';
import {
  countLabel,
  formatDurationMs,
} from 'src/routes/Settings/AIAssistant/replyUsage';
import { formatCurrency, formatNumber, formatPercent } from 'src/utils';

export interface IAdminStatsSectionsProps {
  overview: AdminStatsOverviewDto | null;
  daily: AdminStatsDailyPointDto[];
  loading: boolean;
  dateRange: DatesRangeValue;
  /** Set when the view is scoped to one user: hides "active users" and scopes Feedback links. */
  userId?: number;
}

export const AdminStatsSections: React.FC<IAdminStatsSectionsProps> = ({
  overview,
  daily,
  loading,
  dateRange,
  userId,
}) => {
  const usageMetrics: IStatSectionMetric[] = [
    {
      label: countLabel(overview?.requestCount, 'request'),
      value: formatNumber(overview?.requestCount),
    },
    ...(userId === undefined
      ? [
          {
            label: countLabel(overview?.activeUserCount, 'active user'),
            value: formatNumber(overview?.activeUserCount),
          },
        ]
      : []),
    {
      label: countLabel(overview?.conversationCount, 'conversation'),
      value: formatNumber(overview?.conversationCount),
    },
    {
      label: countLabel(overview?.inputTokens, 'input token'),
      value: formatNumber(overview?.inputTokens),
    },
    {
      label: countLabel(overview?.outputTokens, 'output token'),
      value: formatNumber(overview?.outputTokens),
    },
    {
      label: 'cache hit rate',
      value: formatPercent(overview?.cacheHitRatio),
    },
  ];

  const attempts = overview?.sqlAttemptCount ?? 0;
  const rejected = overview?.sqlRejectedCount ?? 0;
  const sqlMetrics: IStatSectionMetric[] = [
    {
      label: `rejected (${formatPercent(
        attempts > 0 ? rejected / attempts : 0,
      )})`,
      value: formatNumber(rejected),
    },
    {
      label: countLabel(overview?.sqlErrorCount, 'error'),
      value: formatNumber(overview?.sqlErrorCount),
    },
    { label: 'avg', value: formatDurationMs(overview?.sqlAvgDurationMs) },
    { label: 'p95', value: formatDurationMs(overview?.sqlP95DurationMs) },
  ];

  const effortBreakdown = overview?.reasoningEffortBreakdown ?? [];

  return (
    <Stack gap="md">
      <StatSectionCard
        title="Usage & cost"
        value={formatCurrency(overview?.estCostUsd, { precise: true })}
        valueLabel="est. cost"
        metrics={usageMetrics}
        loading={loading}
      >
        <CostTrendChart daily={daily} />
        {!loading && effortBreakdown.length > 0 && (
          <ReasoningEffortTable rows={effortBreakdown} />
        )}
      </StatSectionCard>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <AnswerQualityCard
          overview={overview}
          loading={loading}
          dateRange={dateRange}
          userId={userId}
        />
        <StatSectionCard
          title="SQL health"
          value={formatNumber(overview?.sqlAttemptCount)}
          valueLabel={countLabel(overview?.sqlAttemptCount, 'attempt')}
          metrics={sqlMetrics}
          loading={loading}
        >
          <SqlHealthChart daily={daily} />
        </StatSectionCard>
      </SimpleGrid>
      <ToolUseCard overview={overview} loading={loading} />
    </Stack>
  );
};
